/**
 * The version editor's arithmetic: what a draft save sends, what a publish
 * still needs, and whether closing would lose anything (Jon, 2026-10-16).
 *
 * ── Why drafts exist ─────────────────────────────────────────────────
 *
 * A published version is immutable and auth fans it out the moment it is
 * written, so the only way to work on one over several sittings used to be a
 * text file somewhere else. A draft is that text file, kept in auth: never
 * public, never owed, never counted as a version, and deleted by publishing it.
 *
 * ── One form, two destinations ───────────────────────────────────────
 *
 * The form is the same whether it saves a draft or publishes. A draft takes
 * anything, including an empty form, so the save sends EVERY field, with ''
 * and null for blanks: a field left out of a PATCH keeps its old value, which
 * would make clearing the summary a save that silently did nothing.
 */
import type { AgreementDraft, DraftBody } from './api.js';

/** Auth's limit on a version's summary, which is markdown now. */
export const SUMMARY_LIMIT = 10_000;

/** What the editor holds while it is open. `effectiveAt` is the picker's
 *  local wall-clock string, `YYYY-MM-DDTHH:mm`, or '' when unset. */
export type VersionForm = {
  version: string;
  content: string;
  summary: string;
  effectiveAt: string;
  requiresReacceptance: boolean;
};

export const EMPTY_FORM: VersionForm = {
  version: '',
  content: '',
  summary: '',
  effectiveAt: '',
  requiresReacceptance: true,
};

/**
 * An ISO instant as the datetime-local picker wants it: local wall-clock,
 * minutes, no zone. The inverse of `new Date(local).toISOString()`, so a
 * draft saved and reopened shows the time that was typed, not UTC.
 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The picker's local string as the instant auth stores. The server parses a
 *  bare string as UTC, which would shift the date by the reader's offset. */
export function toInstant(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formFromDraft(d: AgreementDraft): VersionForm {
  return {
    version: d.version ?? '',
    content: d.content ?? '',
    summary: d.summary ?? '',
    effectiveAt: toLocalInput(d.effectiveAt),
    requiresReacceptance: d.requiresReacceptance,
  };
}

/** The draft POST and PATCH body. `requiresReacceptance` is left out for a
 *  notice, as on publish, so the request says what the form showed. */
export function draftBody(f: VersionForm, notice: boolean): DraftBody {
  return {
    version: f.version.trim(),
    content: f.content,
    contentFormat: 'markdown',
    summary: f.summary.trim(),
    effectiveAt: toInstant(f.effectiveAt),
    ...(notice ? {} : { requiresReacceptance: f.requiresReacceptance }),
  };
}

/** What a publish still needs, in the form's order, by the labels on screen.
 *  Empty when it can go. Auth refuses the same with DRAFT_INCOMPLETE. */
export function missingForPublish(f: VersionForm): string[] {
  const missing: string[] = [];
  if (!f.version.trim()) missing.push('Version');
  if (!f.content.trim()) missing.push('Content');
  if (!toInstant(f.effectiveAt)) missing.push('Effective from');
  return missing;
}

export function summaryProblem(summary: string): string | null {
  return summary.length > SUMMARY_LIMIT
    ? `The summary is ${summary.length.toLocaleString('en-US')} characters; the limit is ${SUMMARY_LIMIT.toLocaleString('en-US')}.`
    : null;
}

/** Whether closing now would lose typing. Compared to the last save, or to
 *  the empty form for a version never saved. A notice ignores the checkbox. */
export function isDirty(f: VersionForm, saved: VersionForm, notice: boolean): boolean {
  return (
    f.version !== saved.version ||
    f.content !== saved.content ||
    f.summary !== saved.summary ||
    f.effectiveAt !== saved.effectiveAt ||
    (!notice && f.requiresReacceptance !== saved.requiresReacceptance)
  );
}

/** A draft's name in the list: its version, or "untitled" before it has one. */
export function draftLabel(d: { version: string | null }): string {
  return d.version?.trim() || 'untitled';
}
